import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, TrendingUp, Users, Clock, ChevronRight, Shield, MapPin } from "lucide-react";

// ═══════════════════════════════════════════════════════════
// PROFESSIONAL INVESTMENT COLORS - FINTECH STYLE
// ═══════════════════════════════════════════════════════════
const COLORS = {
  // Primary palette - Dark, trustworthy
  bgDark: "#0A0F1C",
  bgCard: "#111827",
  bgCardLight: "#1F2937",
  bgAccent: "#162234",
  
  // Brand accent
  primary: "#10B981", // Emerald green - growth/money
  primaryLight: "#34D399",
  primaryDark: "#059669",
  
  // Secondary
  gold: "#F59E0B",
  goldLight: "#FBBF24",
  
  // Text
  white: "#FFFFFF",
  textPrimary: "#F9FAFB",
  textSecondary: "#9CA3AF",
  textMuted: "#6B7280",
  
  // Utility
  border: "#374151",
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
};

// ═══════════════════════════════════════════════════════════
// INVESTMENT CITIES DATA - ALL COMING SOON FOR LAUNCH
// ═══════════════════════════════════════════════════════════
const CITIES = [
  {
    id: "koeln",
    name: "Köln Ehrenfeld",
    subtitle: "Kreatives Zentrum im Herzen von Köln",
    image: "https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?w=800",
    fundingGoal: 100000,
    currentFunding: 0,
    investors: 0,
    daysLeft: null,
    status: "coming_soon", // ALL coming_soon for launch
    featured: true,
  },
  {
    id: "berlin",
    name: "Berlin Kreuzberg",
    subtitle: "Kulturelles Epizentrum der Hauptstadt",
    image: "https://images.unsplash.com/photo-1560969184-10fe8719e047?w=800",
    fundingGoal: 150000,
    currentFunding: 0,
    investors: 0,
    daysLeft: null,
    status: "coming_soon",
    featured: false,
  },
  {
    id: "muenchen",
    name: "München Schwabing",
    subtitle: "Premium-Standort im Herzen Bayerns",
    image: "https://images.unsplash.com/photo-1595867818082-083862f3d630?w=800",
    fundingGoal: 120000,
    currentFunding: 0,
    investors: 0,
    daysLeft: null,
    status: "coming_soon",
    featured: false,
  },
];

// ═══════════════════════════════════════════════════════════
// CITY CARD COMPONENT
// ═══════════════════════════════════════════════════════════
const CityCard = ({ city, onClick }) => {
  const progress = (city.currentFunding / city.fundingGoal) * 100;
  const isActive = city.status === "active";
  const isFunded = city.status === "funded";
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="rounded-2xl overflow-hidden cursor-pointer"
      style={{ 
        backgroundColor: COLORS.bgCard,
        border: city.featured ? `1px solid ${COLORS.primary}40` : `1px solid ${COLORS.border}`,
      }}
      data-testid={`city-card-${city.id}`}
    >
      {/* Image */}
      <div className="relative h-40 overflow-hidden">
        <img 
          src={city.image} 
          alt={city.name}
          className="w-full h-full object-cover"
        />
        <div 
          className="absolute inset-0"
          style={{ background: "linear-gradient(to top, rgba(10,15,28,0.9) 0%, transparent 60%)" }}
        />
        
        {/* Status Badge */}
        <div className="absolute top-3 left-3">
          {isActive && (
            <span 
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
            >
              Aktiv
            </span>
          )}
          {city.status === "coming_soon" && (
            <span 
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: COLORS.bgCardLight, color: COLORS.textSecondary }}
            >
              Demnächst
            </span>
          )}
          {isFunded && (
            <span 
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: COLORS.gold, color: COLORS.bgDark }}
            >
              Finanziert
            </span>
          )}
        </div>

        {city.featured && (
          <div className="absolute top-3 right-3">
            <span 
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: COLORS.gold, color: COLORS.bgDark }}
            >
              Featured
            </span>
          </div>
        )}

        {/* Title overlay */}
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="text-lg font-bold" style={{ color: COLORS.white }}>
            {city.name}
          </h3>
          <p className="text-sm" style={{ color: COLORS.textSecondary }}>
            {city.subtitle}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isActive ? (
          <>
            {/* Progress Bar */}
            <div className="mb-3">
              <div 
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: COLORS.bgCardLight }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: COLORS.primary }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className="font-bold" style={{ color: COLORS.primary }}>
                  {city.currentFunding.toLocaleString('de-DE')}€
                </span>
                <span style={{ color: COLORS.textMuted }}>
                  {' '}/ {city.fundingGoal.toLocaleString('de-DE')}€
                </span>
              </div>
              <span 
                className="font-semibold"
                style={{ color: COLORS.textPrimary }}
              >
                {Math.round(progress)}%
              </span>
            </div>

            {/* Meta */}
            <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.border}` }}>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" style={{ color: COLORS.textMuted }} />
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>
                  {city.investors} Investor:innen
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" style={{ color: COLORS.textMuted }} />
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>
                  {city.daysLeft} Tage
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="py-2">
            <p className="text-sm" style={{ color: COLORS.textMuted }}>
              {city.status === "coming_soon" 
                ? "Funding startet bald. Lass dich benachrichtigen."
                : "Erfolgreich finanziert."
              }
            </p>
            {city.status === "coming_soon" && (
              <button 
                className="mt-3 w-full py-2 rounded-lg text-sm font-medium"
                style={{ 
                  backgroundColor: COLORS.bgCardLight,
                  color: COLORS.textPrimary,
                  border: `1px solid ${COLORS.border}`
                }}
              >
                Benachrichtigen
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN INVEST OVERVIEW PAGE
// ═══════════════════════════════════════════════════════════
export default function InvestOverviewPage() {
  const navigate = useNavigate();

  const activeCities = CITIES.filter(c => c.status === "active");
  const upcomingCities = CITIES.filter(c => c.status === "coming_soon");

  return (
    <div 
      className="min-h-screen pb-8"
      style={{ backgroundColor: COLORS.bgDark }}
    >
      {/* Header */}
      <header 
        className="px-4 pt-6 pb-4 flex items-center gap-3"
        style={{ backgroundColor: COLORS.bgDark }}
      >
        <motion.button 
          whileTap={{ scale: 0.9 }} 
          onClick={() => navigate("/landing")}
          className="p-2 rounded-xl"
          style={{ backgroundColor: COLORS.bgCardLight }}
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: COLORS.textPrimary }} />
        </motion.button>
        <div>
          <h1 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
            Investieren
          </h1>
          <p className="text-xs" style={{ color: COLORS.textMuted }}>
            Werde Teil von Franky's Farm
          </p>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-5 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-2xl relative overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.bgCard} 0%, ${COLORS.bgAccent} 100%)`,
            border: `1px solid ${COLORS.border}`
          }}
        >
          {/* Decorative elements */}
          <div 
            className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-10"
            style={{ backgroundColor: COLORS.primary }}
          />
          
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5" style={{ color: COLORS.primary }} />
              <span className="text-sm font-medium" style={{ color: COLORS.primary }}>
                Investment Opportunität
              </span>
            </div>
            
            <h2 className="text-2xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Investiere in die Zukunft
            </h2>
            <p className="text-sm mb-4" style={{ color: COLORS.textSecondary }}>
              Werde Miteigentümer:in einer Franky's Farm Filiale und profitiere 
              von 20% der jährlichen Gewinne.
            </p>

            {/* Key Stats */}
            <div className="grid grid-cols-3 gap-3 mt-4 pt-4" style={{ borderTop: `1px solid ${COLORS.border}` }}>
              <div className="text-center">
                <p className="text-xl font-bold" style={{ color: COLORS.primary }}>20%</p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>Gewinnbeteiligung</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold" style={{ color: COLORS.textPrimary }}>100€</p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>Mindestinvestment</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold" style={{ color: COLORS.gold }}>127</p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>Investor:innen</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Active Investments */}
      {activeCities.length > 0 && (
        <section className="px-5 py-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
              Aktive Investments
            </h3>
            <span 
              className="px-2 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: `${COLORS.primary}20`, color: COLORS.primary }}
            >
              {activeCities.length} verfügbar
            </span>
          </div>
          
          <div className="space-y-4">
            {activeCities.map((city, i) => (
              <CityCard 
                key={city.id}
                city={city}
                onClick={() => navigate(`/invest/city/${city.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming */}
      {upcomingCities.length > 0 && (
        <section className="px-5 py-4">
          <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
            Demnächst
          </h3>
          
          <div className="space-y-4">
            {upcomingCities.map((city, i) => (
              <CityCard 
                key={city.id}
                city={city}
                onClick={() => {}}
              />
            ))}
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section className="px-5 py-6">
        <div 
          className="p-4 rounded-xl"
          style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
        >
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: COLORS.gold }} />
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: COLORS.textPrimary }}>
                Transparente Beteiligung
              </p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>
                Alle Investments werden transparent dokumentiert. 
                Du erhältst regelmäßige Updates und Berichte über die Entwicklung deiner Filiale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Notice */}
      <section className="px-5 py-4">
        <div 
          className="p-4 rounded-xl"
          style={{ backgroundColor: `${COLORS.warning}10`, border: `1px solid ${COLORS.warning}30` }}
        >
          <p className="text-xs" style={{ color: COLORS.textSecondary }}>
            <span className="font-semibold" style={{ color: COLORS.warning }}>Risikohinweis:</span>{' '}
            Dies ist eine unternehmerische Beteiligung mit Risiko. 
            Es besteht die Möglichkeit eines vollständigen Verlustes des eingesetzten Kapitals.
          </p>
        </div>
      </section>
    </div>
  );
}

export { COLORS, CITIES };
